import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  DatePicker,
  Modal,
  Form,
  Input,
  Radio,
  AutoComplete,
  InputNumber,
} from 'antd';
import { partList } from './data/general';
import { AppStore } from 'stores';

const { TextArea } = Input;

const CustomServiceForm = ({
  visible,
  onCancel,
  onCreate,
  serviceName,
  childRef,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (childRef) {
      childRef({ validateForm });
    }
  }, []);

  const validateForm = async () => {
    try {
      await form.validateFields();
    } catch {
      AppStore.addError('Some fields in the form are required!');
    }
  };

  const renderForm = () => {
    return (
      <Form form={form} layout="vertical">
        {serviceName === 'newIssueReport' && (
          <Form.Item
            name="reportedBy"
            label="Reported By"
            rules={[
              {
                required: true,
                message: 'Please input name of who reported',
              },
            ]}
          >
            <TextArea />
          </Form.Item>
        )}

        <Form.Item
          name="action"
          label="Action / Service Name"
          rules={[
            {
              required: true,
              message: 'Please input a Service name!',
            },
          ]}
        >
          <AutoComplete
            options={[
              'Change',
              'Inspect',
              'Perform',
              'Replace',
              'Remind',
              'Rotate',
            ].map((value) => ({ value }))}
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
              -1
            }
          />
        </Form.Item>

        <Form.Item
          name="partName"
          label={
            serviceName.includes('custom-routine') ? 'Activity' : 'Part Name'
          }
          rules={[
            {
              required: true,
              message: 'Please input a Part Name!',
            },
          ]}
        >
          <AutoComplete
            options={partList.map((value) => ({ value }))}
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
              -1
            }
          />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea autoSize={{ minRows: 3, maxRows: 5 }} />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true }]}
        >
          <Radio.Group size="large">
            <Radio value="Minor">
              <p
                style={{
                  backgroundColor: '#dec431',
                  color: 'white',
                  display: 'inline',
                  padding: '5px',
                  borderRadius: '3px',
                }}
              >
                Minor
              </p>
            </Radio>
            <Radio value="Major">
              <p
                style={{
                  backgroundColor: '#e28931',
                  color: 'white',
                  display: 'inline',
                  padding: '5px',
                  borderRadius: '3px',
                }}
              >
                Major
              </p>
            </Radio>
            <Radio value="Critical">
              <p
                style={{
                  backgroundColor: '#f92d2e',
                  color: 'white',
                  display: 'inline',
                  padding: '5px',
                  borderRadius: '3px',
                }}
              >
                Critical
              </p>
            </Radio>
          </Radio.Group>
        </Form.Item>

        {serviceName === 'serviceHistoryForm' && (
          <>
            <Form.Item
              name="performedDate"
              label="Service Performed Date"
              initialValue={moment()}
              rules={[{ required: true, message: 'Field is required' }]}
            >
              <DatePicker />
            </Form.Item>

            <Form.Item name="causeDescription" label="Cause Description">
              <TextArea />
            </Form.Item>

            <Form.Item
              name="complaintsDescription"
              label="Complaints Description"
            >
              <TextArea />
            </Form.Item>

            <Form.Item
              name="correctedDescription"
              label="Corrected Description"
            >
              <TextArea />
            </Form.Item>

            <Form.Item
              name="totalCost"
              label="Total Cost (Labor + Parts)"
              initialValue={1}
              rules={[
                {
                  required: true,
                  type: 'number',
                  message: 'Field is required and must be a number!',
                },
              ]}
            >
              <InputNumber min={1} />
            </Form.Item>

            <Form.Item
              name="laborHours"
              label="Labor Hours"
              initialValue={1}
              rules={[
                {
                  required: true,
                  type: 'number',
                  message: 'Field is required and must be a number!',
                },
              ]}
            >
              <InputNumber min={1} />
            </Form.Item>
          </>
        )}

        {serviceName === 'customIssue' && (
          <Form.Item name="status" label="Status">
            <Radio.Group>
              <Radio.Button value="waitingForApproval" size="large">
                Waiting for Invoice
              </Radio.Button>
              <Radio.Button value="new" size="large">
                Current
              </Radio.Button>
              <Radio.Button value="done" size="large">
                Done
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        )}
      </Form>
    );
  };

  return (
    <div>
      {!serviceName.includes('customIssue') ? (
        renderForm()
      ) : (
        <Modal
          open={visible}
          title="Create a Custom Service"
          okText="Create"
          onCancel={onCancel}
          onOk={() => {
            form
              .validateFields()
              .then((values) => {
                onCreate(values);
                form.resetFields();
              })
              .catch(() => {
                AppStore.addError('Please fill in all the required fields');
              });
          }}
        >
          {renderForm()}
        </Modal>
      )}
    </div>
  );
};

CustomServiceForm.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onCreate: PropTypes.func,
  serviceName: PropTypes.string,
  childRef: PropTypes.func,
};

export default CustomServiceForm;
